import React, { useCallback, useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';
import Thumbnail from '@amzn/meridian/thumbnail';
import Breadcrumbs from 'src/components/shared/breadcrumbs/breadcrumbs';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import { COUNTRY_ID_MAPPING } from 'src/config/CountryIdMapping';
import { BADGE_PHOTO_THUMBNAIL_URL } from 'src/config/ExternalLinks';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import associateProfileService from 'src/services/associateProfile/associateProfileService';
import type { AssociateProfile } from 'src/services/associateProfile/types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createToast } from 'src/store/toastsSlice';

import './associateProfile.scss';
import AssociateProfileEmployeeInfo from './associateProfileEmployeeInfo';
import EmDash from './emdash';
import ModifiedDuty from './modifiedDuty';

import AssociateProfilePlacements from './placements/associateProfilePlacements';

type TabType = 'tab-placements' | 'tab-restrictions' | 'tab-history';

const AssociatesProfile = () => {
  const { alias } = useParams();
  const { selectedSite } = useAppSelector((state) => state.sites);
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState<TabType>('tab-placements');
  const [associateProfile, setAssociateProfile] = useState<AssociateProfile | null>(null);

  const openNewJmrInNewTab = useCallback(() => {
    window.open(`${window.location.origin}/newJmr/${associateProfile?.alias}`, '_blank', 'noopener,noreferrer');
  }, [associateProfile?.alias]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!alias || selectedSite.id === 0) return;

      try {
        const { data } = await associateProfileService.getAssociateProfile(alias, selectedSite.id);
        setAssociateProfile(data);
      } catch {
        dispatch(
          createToast({
            type: 'error',
            message: 'Invalid Alias',
            timeout: TOAST_TIMEOUT,
          }),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [alias, selectedSite.id, dispatch]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="associate-profile">
      <Breadcrumbs
        breadcrumbList={[
          { url: '/placementsOverview', label: 'Placements Overview' },
          { url: `/associateProfile/${associateProfile?.alias}`, label: `Associate ${associateProfile?.name}` },
        ]}
      />
      <div className="associate-profile__header">
        <Row alignmentVertical="center">
          <Thumbnail source={BADGE_PHOTO_THUMBNAIL_URL + associateProfile?.alias} />
          <Box>
            <Text type="h400">{associateProfile?.name ?? <EmDash />}</Text>
            <Text type="b300">
              {associateProfile?.jobTitle ?? <EmDash />} &bull; &nbsp;
              {associateProfile?.alias ? (
                <Link
                  type="secondary"
                  href={`https://fclm-portal.amazon.com/search?term=${associateProfile?.alias}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >{`${associateProfile?.alias}@`}</Link>
              ) : (
                <EmDash />
              )}
            </Text>
          </Box>
          <Button className="create-placement-button" onClick={openNewJmrInNewTab}>
            Create Placement
          </Button>
        </Row>
        <Row className="associate-profile__info" spacing="500">
          <AssociateProfileEmployeeInfo label="Employee ID" value={associateProfile?.employeeId} />
          <AssociateProfileEmployeeInfo
            label="Manager"
            value={associateProfile?.managersName}
            url={`https://phonetool.amazon.com/users/${associateProfile?.managersAlias}`}
          />
          <AssociateProfileEmployeeInfo label="Home Location" value={associateProfile?.location} />
          <AssociateProfileEmployeeInfo label="Shift" value={associateProfile?.shift} />
          {selectedSite.countryId === COUNTRY_ID_MAPPING.USA_COUNTRY_ID && (
            <ModifiedDuty value={associateProfile?.totalAccommodatedDays} />
          )}
        </Row>
      </div>
      <div className="associate-profile__content">
        <TabGroup value={currentTab} onChange={setCurrentTab}>
          <Tab value="tab-placements" aria-controls="tabpanel-placements" id="tab-placements">
            Placements
          </Tab>
          <Tab value="tab-restrictions" aria-controls="tabpanel-restrictions" id="tab-restrictions">
            Restrictions
          </Tab>
          <Tab value="tab-history" aria-controls="tabpanel-history" id="tab-history">
            History
          </Tab>
        </TabGroup>
        {currentTab === 'tab-placements' && (
          <div id="tabpanel-placements" aria-labelledby="tab-placements">
            <AssociateProfilePlacements />
          </div>
        )}
        {currentTab === 'tab-restrictions' && (
          <div id="tabpanel-restrictions" aria-labelledby="tab-restrictions">
            <Text>Restrictions</Text>
          </div>
        )}
        {currentTab === 'tab-history' && (
          <div id="tabpanel-history" aria-labelledby="tab-history">
            <Text>History</Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssociatesProfile;
